import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faSpinner } from '@fortawesome/pro-regular-svg-icons';
import { Popconfirm } from 'antd';
import { IconButton } from 'src/components/design-system';
import { useAccessiblePopconfirm } from 'src/components/design-system/popconfirm/hooks/use-accessible-popconfirm';
import styles from './cell-renderers.module.scss';
import { colorPalette } from 'shared/constants';

/**
 * Reusable delete action cell for AG Grid tables with accessible Popconfirm.
 *
 * @param {Object} props
 * @param {Function} props.onDelete - Callback when delete is confirmed, receives (event, itemId)
 * @param {string|number} props.itemId - ID of the item to delete
 * @param {boolean} [props.isDeleting=false] - Whether deletion is in progress
 * @param {Object} props.lang - Language strings object
 * @param {string} props.lang.confirmTitle - Confirmation dialog title
 * @param {string} props.lang.deleteLabel - Delete button aria-label
 * @param {string} props.lang.deleteButton - Delete button text
 * @param {string} props.lang.cancelButton - Cancel button text
 * @param {import('@fortawesome/fontawesome-common-types').IconDefinition} [props.icon] - Custom icon (defaults to faTrashAlt)
 * @param {string} [props.className] - Additional className for the wrapper
 */
export function DeleteActionCell({
  onDelete,
  itemId,
  isDeleting = false,
  lang,
  icon = faTrashAlt,
  className,
}) {
  const popconfirmProps = useAccessiblePopconfirm({
    onConfirm: async e => {
      e?.stopPropagation();
      await onDelete(e, itemId);
    },
    onCancel: e => {
      e?.stopPropagation();
    },
  });

  return (
    <div className={className || styles.actionsCell}>
      <Popconfirm
        title={lang.confirmTitle}
        cancelText={lang.cancelButton}
        okText={lang.deleteButton}
        okType="danger"
        okButtonProps={{
          type: 'primary',
          'aria-label': lang.deleteButton,
          loading: isDeleting,
        }}
        cancelButtonProps={{
          type: 'default',
          'aria-label': lang.cancelButton,
        }}
        {...popconfirmProps}>
        <IconButton
          label={lang.deleteLabel}
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
          }}
          disabled={isDeleting}>
          <FontAwesomeIcon
            color={colorPalette.red}
            icon={isDeleting ? faSpinner : icon}
            spin={isDeleting}
          />
        </IconButton>
      </Popconfirm>
    </div>
  );
}
